import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { HiXMark } from "react-icons/hi2";
import { useOverlayStore } from "../../stores/overlayStore";

export const GOALS_SHEET_Z_INDEX_CLASS = "z-[900]";

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Footer actions (Done / Remove). */
  footer?: React.ReactNode;
  busy?: boolean;
  /** Match SessionBottomSheet's width by default; `wide` for editors. */
  wide?: boolean;
}

/**
 * Bottom-sheet shell shared by the Goals editors and the Competition sheet.
 * Same framer-motion backdrop + spring as SessionBottomSheet.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  busy = false,
  wide = false,
}) => {
  const registerBottomSheet = useOverlayStore((s) => s.registerBottomSheet);
  const unregisterBottomSheet = useOverlayStore(
    (s) => s.unregisterBottomSheet,
  );
  useEffect(() => {
    if (!isOpen) return;
    registerBottomSheet();
    return () => unregisterBottomSheet();
  }, [isOpen, registerBottomSheet, unregisterBottomSheet]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="goals-sheet-backdrop"
          className={`fixed inset-0 ${GOALS_SHEET_Z_INDEX_CLASS} bg-black/50 backdrop-blur-sm`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={busy ? undefined : onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={`fixed bottom-0 left-0 right-0 mx-auto w-full ${
              wide ? "sm:max-w-lg" : "sm:max-w-md"
            } bg-base-100 rounded-t-2xl shadow-xl flex flex-col max-h-[92vh]`}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-12 h-1 bg-base-300 rounded-full" />
            </div>
            <div className="flex items-start justify-between gap-3 px-5 pt-1 pb-3">
              <div className="min-w-0">
                <h2 className="text-lg font-heading text-base-content truncate">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-xs text-base-content/60 font-grotesk mt-0.5">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                disabled={busy}
                className="w-11 h-11 -mt-1.5 -mr-2 flex items-center justify-center rounded-full hover:bg-base-200 transition-colors flex-shrink-0 disabled:opacity-50"
                aria-label="Close"
              >
                <HiXMark className="w-5 h-5 text-base-content/60" />
              </button>
            </div>
            <div className="px-5 pb-3 overflow-y-auto space-y-4 min-h-0">
              {children}
            </div>
            {footer && (
              <div className="px-5 pt-2 pb-4 border-t border-base-200 space-y-2">
                {footer}
              </div>
            )}
            <div className="pb-safe" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BottomSheet;
